import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { LanguageProvider } from "./contexts/LanguageContext";
import { AuthProvider } from "./contexts/AuthContext";
import { CartProvider } from "./contexts/CartContext";
import { WishlistProvider } from "./contexts/WishlistContext";
import { Toaster } from "sonner";
import PageTransition from "./components/PageTransition";
import LanguageGate from "./components/LanguageGate";
import CartDrawer from "./components/CartDrawer";

import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import LandingPage from "./pages/LandingPage";
import HomePage from "./pages/HomePage";
import CatalogPage from "./pages/CatalogPage";
import ProductDetailPage from "./pages/ProductDetailPage";
import MemoryBouquetFlow from "./pages/MemoryBouquetFlow";
import CartPage from "./pages/CartPage";
import CheckoutPage from "./pages/CheckoutPage";
import MyOrdersPage from "./pages/MyOrdersPage";
import MyMemoriesPage from "./pages/MyMemoriesPage";
import MemoryPageView from "./pages/MemoryPageView";
import MemoryPageBuilder from "./pages/MemoryPageBuilder";
import AdminDashboard from "./pages/AdminDashboard";
import AdminLayout from "./pages/admin/AdminLayout";
import AdminHome from "./pages/admin/AdminHome";
import AdminLanding from "./pages/admin/AdminLanding";
import AdminMemory from "./pages/admin/AdminMemory";
import AdminAbout from "./pages/admin/AdminAbout";
import AdminSettings from "./pages/admin/AdminSettings";
import AdminMedia from "./pages/admin/AdminMedia";
import AdminCMSOverview from "./pages/admin/AdminCMSOverview";
import AdminApiKeys from "./pages/admin/AdminApiKeys";
import AdminReviews from "./pages/admin/AdminReviews";
import AdminOrdersBoard from "./pages/admin/AdminOrdersBoard";
import AdminVariants from "./pages/admin/AdminVariants";
import AdminProducts from "./pages/admin/AdminProducts";
import AdminCommerce from "./pages/admin/AdminCommerce";
import AdminAnalytics from "./pages/admin/AdminAnalytics";
import AdminInventory from "./pages/admin/AdminInventory";
import AdminStockHistory from "./pages/admin/AdminStockHistory";
import AdminBackup from "./pages/admin/AdminBackup";
import AdminPOS from "./pages/admin/AdminPOS";
import VerifyEmailPage from "./pages/VerifyEmailPage";
import LoyaltyPage from "./pages/LoyaltyPage";
import { HelmetProvider } from "react-helmet-async";
import LoginPage from "./pages/LoginPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import AuthCallback from "./pages/AuthCallback";
import GalleryPage from "./pages/GalleryPage";
import CollectionPage from "./pages/CollectionPage";
import OrderDetailPage from "./pages/OrderDetailPage";
import AboutPage from "./pages/AboutPage";
import GiftCardBuilder from "./pages/GiftCardBuilder";
import UserProfilePage from "./pages/UserProfilePage";
import MemoryBouquetIntro from "./pages/MemoryBouquetIntro";
import WishlistPage from "./pages/WishlistPage";
import NotFoundPage from "./pages/NotFoundPage";

// Routes that render their own immersive nav (no global Navbar/Footer)
const IMMERSIVE_EXACT = [
  "/", "/home", "/catalog", "/about", "/login", "/memory-bouquet", "/memory-bouquet/create",
  "/cart", "/checkout", "/profile", "/orders", "/memories", "/wishlist", "/gallery", "/gift-card", "/404"
];
const isImmersivePath = (pathname) => {
  if (IMMERSIVE_EXACT.includes(pathname)) return true;
  if (pathname.startsWith("/product/")) return true;
  if (pathname.startsWith("/orders/")) return true;
  if (pathname.startsWith("/admin")) return true;
  return false;
};

function AnimatedRoutes() {
  const location = useLocation();

  // CRITICAL: Check for session_id synchronously before rendering routes
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  const isImmersive = isImmersivePath(location.pathname);

  return (
    <>
      {!isImmersive && <Navbar />}
      <main className={isImmersive ? "" : "min-h-screen"}>
        <AnimatePresence mode="wait" initial={false}>
          <Routes location={location} key={location.pathname}>
            <Route path="/" element={<LandingPage />} />
            <Route path="/home" element={<PageTransition><HomePage /></PageTransition>} />
            <Route path="/catalog" element={<PageTransition><CatalogPage /></PageTransition>} />
            <Route path="/product/:productId" element={<PageTransition><ProductDetailPage /></PageTransition>} />
            <Route path="/memory-bouquet" element={<PageTransition><MemoryBouquetIntro /></PageTransition>} />
            <Route path="/memory-bouquet/create" element={<PageTransition><MemoryBouquetFlow /></PageTransition>} />
            <Route path="/cart" element={<PageTransition><CartPage /></PageTransition>} />
            <Route path="/checkout" element={<PageTransition><CheckoutPage /></PageTransition>} />
            <Route path="/orders" element={<PageTransition><MyOrdersPage /></PageTransition>} />
            <Route path="/orders/:orderId" element={<PageTransition><OrderDetailPage /></PageTransition>} />
            <Route path="/memories" element={<PageTransition><MyMemoriesPage /></PageTransition>} />
            <Route path="/memory/:pageId" element={<PageTransition><MemoryPageView /></PageTransition>} />
            <Route path="/memory-builder/:pageId" element={<PageTransition><MemoryPageBuilder /></PageTransition>} />
            <Route path="/gallery" element={<PageTransition><GalleryPage /></PageTransition>} />
            <Route path="/collection/:collectionId" element={<PageTransition><CollectionPage /></PageTransition>} />
            <Route path="/about" element={<PageTransition><AboutPage /></PageTransition>} />
            <Route path="/gift-card" element={<PageTransition><GiftCardBuilder /></PageTransition>} />
            <Route path="/profile" element={<PageTransition><UserProfilePage /></PageTransition>} />
            <Route path="/wishlist" element={<PageTransition><WishlistPage /></PageTransition>} />
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<AdminHome />} />
              <Route path="cms" element={<AdminCMSOverview />} />
              <Route path="landing" element={<AdminLanding />} />
              <Route path="memory" element={<AdminMemory />} />
              <Route path="about" element={<AdminAbout />} />
              <Route path="settings" element={<AdminSettings />} />
              <Route path="media" element={<AdminMedia />} />
              <Route path="api-keys" element={<AdminApiKeys />} />
              <Route path="reviews" element={<AdminReviews />} />
              <Route path="orders-board" element={<AdminOrdersBoard />} />
              <Route path="variants" element={<AdminVariants />} />
              <Route path="products" element={<AdminProducts />} />
              <Route path="commerce" element={<AdminCommerce />} />
              <Route path="analytics" element={<AdminAnalytics />} />
              <Route path="inventory" element={<AdminInventory />} />
              <Route path="inventory/history" element={<AdminStockHistory />} />
              <Route path="backup" element={<AdminBackup />} />
              <Route path="pos" element={<AdminPOS />} />
            </Route>
            <Route path="/verify-email" element={<PageTransition><VerifyEmailPage /></PageTransition>} />
            <Route path="/loyalty" element={<PageTransition><LoyaltyPage /></PageTransition>} />
            <Route path="/admin/legacy" element={<PageTransition><AdminDashboard /></PageTransition>} />
            <Route path="/login" element={<PageTransition><LoginPage /></PageTransition>} />
            <Route path="/forgot-password" element={<PageTransition><ForgotPasswordPage /></PageTransition>} />
            <Route path="/reset-password" element={<PageTransition><ResetPasswordPage /></PageTransition>} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="*" element={<PageTransition><NotFoundPage /></PageTransition>} />
          </Routes>
        </AnimatePresence>
      </main>
      {!isImmersive && <Footer />}
    </>
  );
}

function App() {
  // Force-hide the Emergent badge injected by platform proxy
  useEffect(() => {
    const hideBadge = () => {
      const badge = document.getElementById('emergent-badge');
      if (badge) {
        // Override inline style directly — last writer on same element wins
        badge.style.cssText = 'display:none!important;visibility:hidden!important;opacity:0!important;width:0!important;height:0!important;overflow:hidden!important;position:absolute!important;left:-99999px!important;top:-99999px!important;pointer-events:none!important;clip:rect(0,0,0,0)!important;';
        badge.setAttribute('hidden', '');
        badge.setAttribute('aria-hidden', 'true');
      }
      document.querySelectorAll('a[href*="emergent.sh"], a[href*="emergent-badge"]').forEach(el => {
        el.style.cssText = 'display:none!important;visibility:hidden!important;width:0!important;height:0!important;';
        el.setAttribute('hidden', '');
      });
    };
    // Run immediately and repeatedly
    hideBadge();
    const interval = setInterval(hideBadge, 300);
    // Also observe DOM for any re-injection
    const observer = new MutationObserver(hideBadge);
    observer.observe(document.body, { childList: true, subtree: true, attributes: true });
    return () => {
      clearInterval(interval);
      observer.disconnect();
    };
  }, []);

  return (
    <HelmetProvider>
    <LanguageProvider>
      <BrowserRouter>
        <AuthProvider>
          <CartProvider>
            <WishlistProvider>
              <LanguageGate />
              <AnimatedRoutes />
              <CartDrawer />
              <Toaster position="bottom-right" richColors />
            </WishlistProvider>
          </CartProvider>
        </AuthProvider>
      </BrowserRouter>
    </LanguageProvider>
    </HelmetProvider>
  );
}

export default App;
